import { Alert, Button, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function PwaStatus() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [installPrompt, setInstallPrompt] =
    useState<InstallPromptEvent | null>(null);
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker
  } = useRegisterSW();

  useEffect(() => {
    const markOnline = () => setOnline(true);
    const markOffline = () => setOnline(false);
    const captureInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("online", markOnline);
    window.addEventListener("offline", markOffline);
    window.addEventListener("beforeinstallprompt", captureInstallPrompt);
    return () => {
      window.removeEventListener("online", markOnline);
      window.removeEventListener("offline", markOffline);
      window.removeEventListener("beforeinstallprompt", captureInstallPrompt);
    };
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return <>
    {!online && (
      <Alert severity="error" variant="filled" square>
        {t("transverse.app.pwa.offline")}
      </Alert>
    )}
    <Snackbar open={Boolean(installPrompt)}
      message={t("transverse.app.pwa.installAvailable")}
      action={<Button color="inherit" onClick={() => void install()}>
        {t("transverse.app.pwa.install")}
      </Button>} />
    <Snackbar open={needRefresh}
      message={t("transverse.app.pwa.updateAvailable")}
      action={<Button color="inherit"
        onClick={() => void updateServiceWorker(true)}>
        {t("transverse.app.pwa.update")}
      </Button>}
      onClose={() => setNeedRefresh(false)} />
    <Snackbar open={offlineReady && online}
      autoHideDuration={5000}
      message={t("transverse.app.pwa.ready")}
      onClose={() => setOfflineReady(false)} />
  </>;
}
